/** Optional store details on Home; errors never block using the app. */
import { useCallback, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../../queries/shopifyQueryPolicies'
import type { MerchantOnboardingAnswerInput } from '@testfeed/shopify-contracts'
import {
  buildShopifyAppScopeKey,
  shopifyQueryKey,
  useShopifyAppContext,
  type ShopifyAppContextValue,
} from '../../../ShopifyAppContext'
import { useShopifyScopeLifecycle } from '../../../hooks/useShopifyScopeLifecycle'
import { useShopifyModal } from '../../../hooks/outside/useShopifyModal'
import type { ShopifyAppBridge } from '../../../shopifyApiFetch'
import { trackShopifyAction, trackShopifyRefetch } from '../../../shopifyUiTracking'
import { readOnboardingDetails, writeOnboardingDetails } from './onboardingDetailsApi'
import { OnboardingDetailsForm } from './OnboardingDetailsForm'

/** Only a verified active connection can expose the optional editor. */
export function OnboardingDetails({ shopify }: { shopify: ShopifyAppBridge }) {
  const scope = useShopifyAppContext()
  if (!scope.orgId || !scope.connectionId || scope.shopDomain === 'no-shop') return null
  return <OnboardingDetailsScoped key={buildShopifyAppScopeKey(scope)} shopify={shopify} scope={scope} />
}

function OnboardingDetailsScoped({ shopify, scope }: { shopify: ShopifyAppBridge; scope: ShopifyAppContextValue }) {
  const [open, setOpen] = useState(false)
  const pending = useRef(false)
  const onClose = useCallback(() => setOpen(false), [])
  const modal = useShopifyModal({ open, onClose })
  const queryClient = useQueryClient()
  const { token, isActive } = useShopifyScopeLifecycle(buildShopifyAppScopeKey(scope))
  const connectionId = scope.connectionId!
  const key = shopifyQueryKey(scope.shopDomain, scope.orgId, connectionId, ['merchant-onboarding'])
  const query = useQuery({
    ...shopifyQueryPolicies.integrations,
    queryKey: key,
    enabled: open,
    retry: false,
    queryFn: ({ signal }) => readOnboardingDetails(shopify, scope.shopDomain, connectionId, signal),
  })
  const mutation = useMutation({
    mutationFn: (action: { action: 'finish'; answers: MerchantOnboardingAnswerInput[] }) =>
      writeOnboardingDetails(shopify, scope.shopDomain, connectionId, action),
    onSuccess: (status) => {
      if (isActive(token)) queryClient.setQueryData(key, status)
    },
  })
  const save = async (answers: MerchantOnboardingAnswerInput[]) => {
    if (pending.current || !isActive(token)) return false
    pending.current = true
    try {
      await trackShopifyAction(shopify, 'save-onboarding-details', () =>
        mutation.mutateAsync({ action: 'finish', answers }),
      )
      return isActive(token)
    } catch (cause) {
      console.error('Unable to save store details', cause)
      return false
    } finally {
      pending.current = false
    }
  }
  let body = <s-paragraph>Loading your details…</s-paragraph>
  if (open && !query.isFetching && query.error)
    body = (
      <s-stack gap="base">
        <s-banner tone="critical">Could not load your details.</s-banner>
        <s-button onClick={() => void trackShopifyRefetch(shopify, () => query.refetch())}>Try again</s-button>
      </s-stack>
    )
  if (open && !query.isFetching && query.data)
    body = (
      <OnboardingDetailsForm
        fields={query.data.fields}
        saving={mutation.isPending}
        error={mutation.error ? 'Could not save your details. Please try again.' : null}
        onSave={save}
      />
    )
  return (
    <>
      <s-section heading="Tell us about your store">
        <s-stack gap="base">
          <s-paragraph>Share a few details to help us understand your store. You can do this anytime.</s-paragraph>
          <s-button
            onClick={() =>
              trackShopifyAction(shopify, 'open-info', () => {
                mutation.reset()
                setOpen(true)
              })
            }
          >
            Add store details
          </s-button>
        </s-stack>
      </s-section>
      <s-modal ref={modal as never} id="home-store-details" heading="Store details" size="large">
        {body}
        <s-button slot="secondary-actions" commandFor="home-store-details" command="--hide">
          Close
        </s-button>
      </s-modal>
    </>
  )
}
